"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CalendarRange, ChartLine as LineChart, ShieldAlert, ArrowUpRight, Snowflake, SunMedium, Compass } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const challenges = [{
  id: 1,
  title: "Extreme Seasonality & Gondola Demand",
  subtitle: "Demand management",
  iconName: "CalendarRange",
  description: "Manage intense winter ski rushes and gondola surges against steep shoulder drops. We deploy dynamic minimum length-of-stay (MLOS) rules and pace-based pricing so your mountain resort never resorts to distressed discounting.",
  metricLabel: "Strategy focus",
  metricValue: "Winter peak yield fencing & MLOS optimization"
}, {
  id: 2,
  title: "Aggressive Comp-Set Undercutting",
  subtitle: "Real-time rate intelligence",
  iconName: "LineChart",
  description: "Track intraday rate shifts across MakeMyTrip, Agoda, and Booking.com. Automated competitive tracking and yield thresholds protect your RevPAR while securing high-intent winter and summer bookings ahead of comp-set cuts.",
  metricLabel: "Strategy focus",
  metricValue: "Intraday OTA monitoring & rule-based rate fencing"
}, {
  id: 3,
  title: "B2B Wholesaler Leakage & Parity Disruption",
  subtitle: "Channel distribution",
  iconName: "ShieldAlert",
  description: "Prevent unauthorized B2B tour wholesaler rate leakage across Kashmir itineraries and reclaim margins lost to high OTA commissions. We enforce rate parity across meta-search channels and capture direct premium bookings.",
  metricLabel: "Strategy focus",
  metricValue: "Wholesaler rate fencing & direct booking yield boost"
}];
const seasonalityWindows = [{
  season: "Winter Ski & Snow Peak",
  months: "December – March",
  icon: Snowflake,
  focus: "Maximum ADR & Strict MLOS",
  description: "Peak international & domestic winter sports demand. Strict non-refundable terms, 3-night minimum stays, and aggressive ADR tiering around Phase 1 & 2 Gondola operational dates."
}, {
  season: "Summer Alpine Leisure",
  months: "April – June",
  icon: SunMedium,
  focus: "Occupancy Velocity & Family Bundles",
  description: "Escapism and highland tourism. Dynamic package pricing combining valley transfers, golf, and dining to protect yield against local competition."
}, {
  season: "Autumn & Shoulder Transition",
  months: "July – November",
  icon: Compass,
  focus: "Corporate Retreats & Parity Defense",
  description: "Post-monsoon and autumn foliage travel. Tight distribution controls to avoid wholesale rate bleeding while capturing high-value experiential travelers."
}];
export default function Landscape() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.15
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="hospitalitylandscapegulmarg" data-nav="light" className="relative bg-background py-20 md:py-28 text-foreground">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="mx-auto max-w-3xl text-center mb-16 md:mb-20">
          <Badge variant="outline" className="mb-4 border-secondary/40 text-secondary-foreground">
            Market Dynamics & Yield Protection
          </Badge>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground">
            Navigating the hospitality landscape of Gulmarg
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Gulmarg is one of India&apos;s most lucrative yet volatile highland hospitality markets. With extreme winter ski demand, fluctuating gondola schedules, and rapid seasonal turnover, Sunil Singh implements disciplined, algorithm-backed revenue structures tailored for Gulmarg resorts, chalets, and boutique hotels.
          </p>
        </div>

        {/* 3-Column Card Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mb-14 md:mb-18">
          {challenges.map((item, index) => <motion.div key={item.id} data-index={index} variants={cardVariants} className="h-full flex">
              <Card className="flex flex-col justify-between w-full border border-border bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-300">
                <CardHeader className="gap-3">
                  <div className="flex items-center justify-between">
                    <div className="inline-flex size-12 items-center justify-center rounded-md bg-secondary/10 text-secondary-foreground">
                      {item.iconName === "CalendarRange" && <CalendarRange className="size-6 text-chart-2" />}
                      {item.iconName === "LineChart" && <LineChart className="size-6 text-chart-2" />}
                      {item.iconName === "ShieldAlert" && <ShieldAlert className="size-6 text-chart-2" />}
                    </div>
                    <span className="text-xs font-mono font-medium text-muted-foreground tracking-wider uppercase">
                      0{index + 1}
                    </span>
                  </div>
                  <div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-secondary">
                      {item.subtitle}
                    </span>
                    <CardTitle className="font-serif text-xl sm:text-2xl font-bold mt-1.5 text-foreground leading-snug">
                      {item.title}
                    </CardTitle>
                  </div>
                </CardHeader>

                <CardContent className="space-y-6 pt-2">
                  <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                    {item.description}
                  </CardDescription>

                  <div className="rounded-md border border-border/60 bg-muted/40 p-3.5 text-xs">
                    <p className="font-semibold text-foreground mb-1">
                      {item.metricLabel}
                    </p>
                    <p className="text-muted-foreground">
                      {item.metricValue}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Seasonality Strategy Block */}
        <div className="rounded-xl border border-border bg-card/60 p-6 sm:p-8 lg:p-10 shadow-sm">
          <div className="max-w-3xl mb-8">
            <span className="text-xs font-semibold uppercase tracking-wider text-secondary">
              Revenue Playbook by Demand Cycle
            </span>
            <h3 className="font-serif text-2xl sm:text-3xl font-bold text-foreground mt-1">
              Gulmarg Seasonality & Yield Calibrations
            </h3>
            <p className="text-sm sm:text-base text-muted-foreground mt-2">
              How we adjust distribution channels, rate fences, and length-of-stay minimums across Gulmarg&apos;s primary demand cycles.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {seasonalityWindows.map((item, idx) => {
            const IconComp = item.icon;
            return <div key={item.season} data-index={idx} className="rounded-lg border border-border bg-background p-5 flex flex-col justify-between">
                  <div>
                    <div className="flex items-center gap-3 mb-3">
                      <div className="inline-flex size-9 items-center justify-center rounded-md bg-secondary/15 text-secondary-foreground">
                        <IconComp className="size-5 text-chart-2" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-foreground text-sm">
                          {item.season}
                        </h4>
                        <p className="text-xs text-secondary font-mono">
                          {item.months}
                        </p>
                      </div>
                    </div>
                    <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed mb-4">
                      {item.description}
                    </p>
                  </div>
                  <div className="pt-3 border-t border-border/60 text-xs font-medium text-foreground">
                    <span className="text-muted-foreground">Primary Lever: </span>
                    {item.focus}
                  </div>
                </div>;
          })}
          </div>
        </div>

        {/* Section Bottom CTA / Footnote */}
        <div className="mt-14 md:mt-18 pt-8 border-t border-border/70 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground text-center sm:text-left">
            Need a tailored revenue assessment for your Gulmarg hotel or boutique stay?
          </p>
          <Button asChild variant="default" className="bg-primary text-primary-foreground hover:bg-primary/90">
            <Link to="#" className="inline-flex items-center gap-2">
              <span>Book a Revenue Audit</span>
              <ArrowUpRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}